import { Country, State } from 'country-state-city';
import { HotelData } from 'pages/redux';

const GCS_IMAGE_BASE = 'https://storage.googleapis.com/images_wiegfbiwudjdvh/';

type NamedPlace = { isoCode: string; name: string };

const fold = (value: string) => value.trim().toLowerCase();

const matchNamedPlace = (list: NamedPlace[], stored: string): NamedPlace | undefined => {
  const needle = fold(stored);
  if (!needle) return undefined;
  return list.find((item) => fold(item.name) === needle || fold(item.isoCode) === needle);
};

let countriesCache: NamedPlace[] | null = null;
const statesByCountryIso = new Map<string, NamedPlace[]>();

/** Same country list as signup, so hotel profile names stay consistent. */
export const hotelProfileCountries = (): NamedPlace[] => {
  if (!countriesCache) {
    countriesCache = Country.getAllCountries().map((c) => ({
      isoCode: c.isoCode,
      name: c.name,
    }));
  }
  return countriesCache;
};

export const hotelProfileStatesForCountry = (countryName: string): NamedPlace[] => {
  const country = matchNamedPlace(hotelProfileCountries(), countryName);
  if (!country) return [];
  let states = statesByCountryIso.get(country.isoCode);
  if (!states) {
    states = (State.getStatesOfCountry(country.isoCode) || []).map((s) => ({
      isoCode: s.isoCode,
      name: s.name,
    }));
    statesByCountryIso.set(country.isoCode, states);
  }
  return states;
};

export const hotelProfileCanonicalCountry = (stored: string) => (
  matchNamedPlace(hotelProfileCountries(), stored)?.name || String(stored || '').trim()
);

export const hotelProfileCanonicalRegion = (countryName: string, stored: string) => (
  matchNamedPlace(hotelProfileStatesForCountry(countryName), stored)?.name
    || String(stored || '').trim()
);

/** Keep an unmatched saved value visible in the dropdown until staff pick a listed one. */
export const hotelProfileSelectNames = (options: NamedPlace[], current: string): string[] => {
  const names = options.map((item) => item.name);
  const trimmed = current.trim();
  if (trimmed && !names.some((name) => fold(name) === fold(trimmed))) {
    return [trimmed, ...names];
  }
  return names;
};

export const resolveHotelImageSrc = (img?: string | null) => {
  if (!img) return '';
  if (img.startsWith('data:') || img.startsWith('http')) return img;
  return `${GCS_IMAGE_BASE}${img}`;
};

export const isStoredHotelImageId = (img?: string | null) => (
  Boolean(img && img.startsWith('XXX123XXX-'))
);

export type HotelProfileForm = {
  name: string;
  branch: string;
  suffix: string;
  address: string;
  city: string;
  region: string;
  country: string;
  phoneNumber: string;
  displayEmail: string;
  linkedin: string;
  facebook: string;
  instagram: string;
  twitter: string;
  logo: string;
};

const str = (value?: string | null) => (value == null ? '' : String(value));

export const hotelProfileFromData = (hotel?: HotelData | null): HotelProfileForm => {
  const country = hotelProfileCanonicalCountry(str(hotel?.country));
  return {
    name: str(hotel?.name),
    branch: str(hotel?.branch),
    suffix: str(hotel?.suffix),
    address: str(hotel?.address),
    city: str(hotel?.city),
    region: hotelProfileCanonicalRegion(country, str(hotel?.region)),
    country,
    phoneNumber: str(hotel?.phoneNumber),
    displayEmail: str(hotel?.displayEmail),
    linkedin: str(hotel?.linkedin),
    facebook: str(hotel?.facebook),
    instagram: str(hotel?.instagram),
    twitter: str(hotel?.twitter),
    logo: str(hotel?.logo),
  };
};

export const hotelProfilesEqual = (a: HotelProfileForm, b: HotelProfileForm) => (
  a.name === b.name
  && a.branch === b.branch
  && a.suffix === b.suffix
  && a.address === b.address
  && a.city === b.city
  && a.region === b.region
  && a.country === b.country
  && a.phoneNumber === b.phoneNumber
  && a.displayEmail === b.displayEmail
  && a.linkedin === b.linkedin
  && a.facebook === b.facebook
  && a.instagram === b.instagram
  && a.twitter === b.twitter
  && a.logo === b.logo
);

export const hotelProfileNameSave = (name: string) => (
  name.trim().toLowerCase().split(' ').join('')
);

/** Field ids included in the activity log when those values change. */
export const hotelProfileChangedFieldIds = (
  next: HotelProfileForm,
  saved: HotelProfileForm,
): string[] => {
  const keys: (keyof HotelProfileForm)[] = [
    'name', 'branch', 'suffix', 'address', 'city', 'region', 'country',
    'phoneNumber', 'displayEmail', 'linkedin', 'facebook', 'instagram', 'twitter', 'logo',
  ];
  return keys.filter((key) => next[key] !== saved[key]);
};
